import type {
  EventDecisionBody,
  ExecutionMethod,
  ExecutionOptionView,
  OptionDecisionBody,
  ProductionEventView,
  SetOverrideBody,
} from '@scoutaround/script-breakdown-shared';

import { api } from '../../lib/api.js';
import { useRefusal } from '../../lib/permissions.js';
import { Disclosure } from '../ui/Disclosure.js';
import { EmptyState } from '../ui/EmptyState.js';
import { MoreMenu, type MenuItem } from '../ui/MoreMenu.js';
import { StatusPill } from '../ui/StatusPill.js';
import { usePlan, usePlanWrite, writeError } from './context.js';
import {
  ASSUMPTION_LABELS,
  ASSUMPTION_SOURCE_LABELS,
  BAND_LABELS,
  EVENT_TYPE_LABELS,
  INTERPRETATION_SOURCE_LABELS,
  LINK_LABELS,
  METHODS,
  METHOD_LABELS,
  OPTION_APPLICABILITY_LABELS,
  REVIEW_STATE,
  ROLE_LABELS,
  STATE_LABELS,
  interpretationText,
} from './labels.js';
import { EvidenceQuotes, IncompleteBanner, PlanLoadState, sceneRequirements } from './PlanParts.js';
import { RequirementRow } from './RequirementRow.js';

/**
 * HOW THE SCENE'S EVENTS COULD BE DONE — the Execution lens.
 *
 * Each event the screenplay shows (a fight, a fall, a fire), its beats and who
 * takes part, what the screenplay states (observations) apart from how the
 * reading classified it (interpretations, labelled as such), and the methods it
 * could be done by, each with what it would need. Choosing a method accepts
 * nothing: every requirement keeps its own decision.
 *
 * A POTENTIAL SAFETY REVIEW IS PINNED AT THE TOP, never dimmed and never hidden,
 * whatever method is or is not chosen — a review that depends on the method is
 * exactly the one nobody must lose sight of before the method is picked.
 */
export function ExecutionView({ sceneId }: { sceneId: string }) {
  const { plan } = usePlan();

  if (plan === null) {
    return (
      <section className="ui-panel pp-panel" data-testid="production-execution">
        <div className="ui-panel-body">
          <PlanLoadState testId="production-execution" />
        </div>
      </section>
    );
  }

  const events = plan.events.filter((e) => e.sceneId === sceneId).sort((a, b) => a.orderInType - b.orderInType);
  const safety = sceneRequirements(plan, sceneId).filter((r) => r.requirementType === 'SAFETY_REVIEW');

  return (
    <section className="ui-panel pp-panel" data-testid="production-execution" aria-labelledby="production-execution-title">
      <div className="ui-panel-head">
        <h3 className="ui-panel-title" id="production-execution-title">
          How this scene could be done
        </h3>
      </div>
      <div className="ui-panel-body pp-stack">
        <IncompleteBanner plan={plan} />

        {safety.length === 0 ? null : (
          <div className="pp-pinned" data-testid="production-safety-pinned">
            <h4 className="pp-group-title">Potential safety review — depends on method</h4>
            <ul className="pp-list">
              {safety.map((r) => (
                <RequirementRow key={r.id} requirement={r} sceneId={sceneId} pinned />
              ))}
            </ul>
          </div>
        )}

        {events.length === 0 ? (
          <EmptyState title="No production event read in this scene" testId="production-execution-empty">
            The reading found nothing here that needs planning as an event: no fight, stunt, effect or other action.
          </EmptyState>
        ) : (
          <ul className="pp-list">
            {events.map((event) => (
              <EventCard key={event.id} event={event} options={plan.options.filter((o) => o.eventLineageId === event.lineageId)} />
            ))}
          </ul>
        )}

        <Assumptions sceneId={sceneId} />
      </div>
    </section>
  );
}

function EventCard({ event, options }: { event: ProductionEventView; options: ExecutionOptionView[] }) {
  const { projectId, plan, sceneLabel } = usePlan();
  const refusal = useRefusal('SCRIPT_REVIEW');
  const decide = usePlanWrite((input: Omit<EventDecisionBody, 'rowVersion'>) =>
    api.decidePlanEvent(projectId, event.id, { ...input, rowVersion: event.rowVersion }),
  );
  const id = event.id;
  const status = REVIEW_STATE[event.reviewStatus];
  const blocked = decide.isPending || refusal !== null;

  const items: MenuItem[] = [
    event.reviewStatus === 'REJECTED'
      ? { id: 'restore', label: 'Restore this event', testId: `production-event-restore-${id}`, onSelect: () => decide.mutate({ action: 'RESTORE' }) }
      : { id: 'reject', label: 'Not an event to plan', tone: 'danger' as const, testId: `production-event-reject-${id}`, onSelect: () => decide.mutate({ action: 'REJECT' }) },
    ...(event.reviewStatus === 'NEEDS_REVALIDATION'
      ? [{ id: 'revalidate', label: 'Confirm it as it reads now', testId: `production-event-revalidate-${id}`, onSelect: () => decide.mutate({ action: 'REVALIDATE' }) }]
      : []),
  ].map((item) => (blocked ? { ...item, disabled: true, hint: refusal ?? 'Saving…' } : item));

  /** The earlier event a candidate lineage names, when this draft still has it. */
  const candidateTitle = (lineageId: string) => plan?.events.find((e) => e.lineageId === lineageId && e.id !== id)?.title ?? null;
  const selected = options.find((o) => o.status === 'SELECTED');

  return (
    <li className="pp-event" data-testid={`production-event-${id}`}>
      <div className="pp-req-head">
        <span className="pp-req-name">{event.title}</span>
        <span className="pp-tag">{EVENT_TYPE_LABELS[event.type]}</span>
        <StatusPill state={status.state} label={status.label} size="sm" testId={`production-event-status-${id}`} />
        <span className="mm pp-small">{BAND_LABELS[event.band]}</span>
        <span className="ui-toolbar-end">
          <MoreMenu items={items} testId={`production-event-more-${id}`} label={`Actions for ${event.title}`} />
        </span>
      </div>

      {event.matchStatus === 'AMBIGUOUS' ? (
        <div className="pp-question" data-testid={`production-event-match-${id}`}>
          <p>
            {event.candidateLineageIds.length === 1
              ? `Same event as ${candidateTitle(event.candidateLineageIds[0] ?? '') ?? 'one in the previous draft'}?`
              : 'Same event as one in the previous draft?'}
          </p>
          <div className="ui-toolbar">
            {event.candidateLineageIds.map((lineageId, index) => (
              <button
                key={lineageId}
                type="button"
                className="ctl"
                disabled={blocked}
                title={refusal ?? undefined}
                data-testid={`production-event-confirm-same-${id}-${lineageId}`}
                onClick={() => {
                  decide.mutate({ action: 'CONFIRM_SAME', lineageId });
                }}
              >
                {`Yes, the same as ${candidateTitle(lineageId) ?? (event.candidateLineageIds.length === 1 ? 'that one' : `earlier event ${String(index + 1)}`)}`}
              </button>
            ))}
            <button
              type="button"
              className="ctl"
              disabled={blocked}
              title={refusal ?? undefined}
              data-testid={`production-event-confirm-different-${id}`}
              onClick={() => {
                decide.mutate({ action: 'CONFIRM_DIFFERENT' });
              }}
            >
              No, a different event
            </button>
          </div>
        </div>
      ) : null}

      {event.beats.length === 0 ? null : (
        <ol className="pp-beats" data-testid={`production-event-beats-${id}`}>
          {event.beats.map((beat, index) => (
            <li key={`${String(index)}-${beat.action}`}>{beat.action}</li>
          ))}
        </ol>
      )}

      {event.participants.length === 0 ? null : (
        <ul className="pp-participants" data-testid={`production-event-participants-${id}`}>
          {event.participants.map((p, index) => (
            <li key={`${p.role}-${p.name}-${String(index)}`} data-link={p.link}>
              <strong>{p.name}</strong>
              <span className="mm">{` · ${ROLE_LABELS[p.role]} · ${LINK_LABELS[p.link]}`}</span>
            </li>
          ))}
        </ul>
      )}

      {/* What the screenplay states, then how the reading classified it — never one as the other. */}
      <ul className="pp-plain-list pp-readings">
        {event.observations.statedAge === null ? null : (
          <li data-testid={`production-event-observation-age-${id}`}>
            <span className="pp-observation-tag">Observation</span>
            {`: stated age ${String(event.observations.statedAge.years)}`}
          </li>
        )}
        {event.observations.state === null ? null : (
          <li data-testid={`production-event-observation-state-${id}`}>
            <span className="pp-observation-tag">Observation</span>
            {`: ${event.observations.state.subject} ${STATE_LABELS[event.observations.state.state]}`}
          </li>
        )}
        {event.interpretations.map((interpretation, index) => (
          <li
            key={`${interpretation.key}-${String(index)}`}
            className="pp-interpretation"
            data-testid={`production-event-interpretation-${id}-${interpretation.key}`}
          >
            <span className="pp-interpretation-tag">{`Interpretation (${INTERPRETATION_SOURCE_LABELS[interpretation.source]})`}</span>
            {`: ${interpretationText(interpretation.key, interpretation.value)}`}
          </li>
        ))}
      </ul>

      <Disclosure summary="Evidence" testId={`production-event-evidence-${id}`}>
        <EvidenceQuotes
          spans={[event.evidence, ...event.beats.flatMap((b) => (b.evidence === null ? [] : [b.evidence]))]}
          sceneId={event.sceneId}
          testId={`production-event-quotes-${id}`}
        />
        <p className="mm pp-note">{sceneLabel(event.sceneId)}</p>
      </Disclosure>

      {decide.error === null ? null : (
        <p className="pp-error" role="alert" data-testid={`production-event-error-${id}`}>
          {writeError(decide.error)}
        </p>
      )}

      <div className="pp-options" data-testid={`production-event-options-${id}`}>
        <h5 className="pp-options-title">Ways to do it</h5>
        <p className="mm pp-note">Choosing a method accepts nothing: each requirement keeps its own decision.</p>
        {options.length === 0 ? (
          <p className="mm pp-note">The plan has no method for this event.</p>
        ) : (
          <ul className="pp-list">
            {options.map((option) => (
              <OptionCard key={option.id} option={option} otherSelected={selected !== undefined && selected.id !== option.id} />
            ))}
          </ul>
        )}
      </div>

      <OverrideControl scope="EVENT" scopeId={event.lineageId} testId={`production-override-event-${id}`} label="Preferred method for this event" />
    </li>
  );
}

function OptionCard({ option, otherSelected }: { option: ExecutionOptionView; otherSelected: boolean }) {
  const { projectId, plan } = usePlan();
  const refusal = useRefusal('SCRIPT_REVIEW');
  const decide = usePlanWrite((action: OptionDecisionBody['action']) =>
    api.decidePlanOption(projectId, option.id, { action, rowVersion: option.rowVersion }),
  );
  const id = option.id;
  const blocked = decide.isPending || refusal !== null;

  const nameOf = (requirementKey: string, requirementId: string | null) =>
    plan?.requirements.find((r) => r.id === requirementId || r.requirementKey === requirementKey)?.name ??
    'A requirement not in this draft';

  const items: MenuItem[] = [
    option.status === 'REJECTED'
      ? { id: 'restore', label: 'Restore this method', testId: `production-option-restore-${id}`, onSelect: () => decide.mutate('RESTORE') }
      : { id: 'reject', label: 'Rule this method out', tone: 'danger' as const, testId: `production-option-reject-${id}`, onSelect: () => decide.mutate('REJECT') },
  ].map((item) => (blocked ? { ...item, disabled: true, hint: refusal ?? 'Saving…' } : item));

  return (
    <li className="pp-option" data-testid={`production-option-${id}`} data-status={option.status}>
      <div className="pp-req-head">
        <span className="pp-req-name">{METHOD_LABELS[option.method]}</span>
        {option.status === 'SELECTED' ? <StatusPill state="CONFIRMED" label="Chosen" size="sm" testId={`production-option-chosen-${id}`} /> : null}
        {option.status === 'REJECTED' ? <StatusPill state="EXCLUDED" label="Ruled out" size="sm" /> : null}
        {option.matchesPreference ? (
          <span className="pp-tag" data-testid={`production-option-preference-${id}`}>
            Matches your preference
          </span>
        ) : null}
        <span className="ui-toolbar-end">
          {option.status === 'SELECTED' ? (
            <button
              type="button"
              className="ctl"
              disabled={blocked}
              title={refusal ?? undefined}
              data-testid={`production-option-unselect-${id}`}
              onClick={() => {
                decide.mutate('UNSELECT');
              }}
            >
              Clear the choice
            </button>
          ) : option.status === 'PROPOSED' ? (
            <button
              type="button"
              className="ctl"
              disabled={blocked}
              title={refusal ?? undefined}
              data-testid={`production-option-select-${id}`}
              onClick={() => {
                decide.mutate('SELECT');
              }}
            >
              {otherSelected ? 'Choose this instead' : 'Choose this method'}
            </button>
          ) : null}
          <MoreMenu items={items} testId={`production-option-more-${id}`} label={`More for ${METHOD_LABELS[option.method]}`} />
        </span>
      </div>

      {option.needsRevalidation ? (
        <div className="pp-question" data-testid={`production-option-revalidation-${id}`}>
          <p>The event changed since this method was chosen — confirm again.</p>
          <button
            type="button"
            className="ctl"
            disabled={blocked}
            title={refusal ?? undefined}
            data-testid={`production-option-revalidate-${id}`}
            onClick={() => {
              decide.mutate('REVALIDATE');
            }}
          >
            Confirm again
          </button>
        </div>
      ) : null}

      <p className="pp-req-need">{option.description}</p>
      <dl className="pp-facts">
        {option.advantages.length === 0 ? null : <Fact label="Advantages" items={option.advantages} />}
        {option.constraints.length === 0 ? null : <Fact label="Constraints" items={option.constraints} />}
        {option.risks.length === 0 ? null : <Fact label="Risks" items={option.risks} />}
        {option.assumptions.length === 0 ? null : <Fact label="Assumes" items={option.assumptions} />}
      </dl>

      {option.dependencies.length === 0 ? null : (
        <table className="pp-matrix" data-testid={`production-option-dependencies-${id}`}>
          <caption className="mm">What this method would need</caption>
          <tbody>
            {option.dependencies.map((d) => (
              <tr key={d.requirementKey}>
                <th scope="row">{nameOf(d.requirementKey, d.requirementId)}</th>
                <td data-applicability={d.applicability}>{OPTION_APPLICABILITY_LABELS[d.applicability]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {decide.error === null ? null : (
        <p className="pp-error" role="alert" data-testid={`production-option-error-${id}`}>
          {writeError(decide.error)}
        </p>
      )}
    </li>
  );
}

function Fact({ label, items }: { label: string; items: readonly string[] }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>
        <ul className="pp-plain-list">
          {items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </dd>
    </div>
  );
}

/** The project assumptions in effect, and this scene's own execution preference. */
function Assumptions({ sceneId }: { sceneId: string }) {
  const { plan } = usePlan();
  if (plan === null) return null;

  return (
    <div className="pp-assumptions" data-testid="production-assumptions">
      <h4 className="pp-group-title">Assumptions in effect</h4>
      <dl className="pp-facts">
        {plan.assumptions.map((a) => (
          <div key={a.key} data-testid={`production-assumption-${a.key}`}>
            <dt>{ASSUMPTION_LABELS[a.key]}</dt>
            <dd>
              {a.value ?? 'Not set'}
              {a.value === null ? null : <span className="mm">{` · ${ASSUMPTION_SOURCE_LABELS[a.source]}`}</span>}
            </dd>
          </div>
        ))}
      </dl>
      <OverrideControl scope="SCENE" scopeId={sceneId} testId="production-override-scene" label="Preferred method for this scene" />
    </div>
  );
}

/**
 * A preferred method for one scene or one event: a MARK on the methods that
 * match it, never a filter. "No preference" removes the override, and the
 * production's own leaning (if any) applies again.
 */
function OverrideControl({
  scope,
  scopeId,
  testId,
  label,
}: {
  scope: SetOverrideBody['scope'];
  scopeId: string;
  testId: string;
  label: string;
}) {
  const { projectId, scriptVersionId, plan } = usePlan();
  const refusal = useRefusal('SCRIPT_REVIEW');
  const write = usePlanWrite((value: ExecutionMethod | null) =>
    api.setPlanOverride(projectId, { scriptVersionId, scope, scopeId, key: 'EXECUTION_PREFERENCE', value }),
  );
  const current =
    plan?.overrides.find((o) => o.scope === scope && o.scopeId === scopeId && o.key === 'EXECUTION_PREFERENCE')?.value ?? '';

  return (
    <div className="pp-override">
      <label className="pp-field pp-inline">
        <span>{label}</span>
        <select
          value={current}
          disabled={write.isPending || refusal !== null}
          title={refusal ?? undefined}
          data-testid={testId}
          onChange={(e) => {
            write.mutate(METHODS.find((m) => m === e.target.value) ?? null);
          }}
        >
          <option value="">No preference</option>
          {METHODS.map((m) => (
            <option key={m} value={m}>
              {METHOD_LABELS[m]}
            </option>
          ))}
        </select>
      </label>
      {write.error === null ? null : (
        <p className="pp-error" role="alert">
          {writeError(write.error)}
        </p>
      )}
    </div>
  );
}
